import type { ClassAssignmentStoreModel } from './class-assignment.model';

export type AssignmentPhase = 'DRAFT' | 'SCHEDULED' | 'PUBLISHED_ACTIVE' | 'PUBLISHED_DUE';

export interface AssignmentPermissions {
  canEditMetadata: boolean;
  canEditQuestions: boolean;
  canDelete: boolean;
  canOpenBuilder: boolean;
  canDuplicate: boolean;
}

export interface AssignmentPermissionContext {
  status: ClassAssignmentStoreModel['status'];
  due_at?: ClassAssignmentStoreModel['due_at'];
  isDue?: boolean;
}

const ASSIGNMENT_PERMISSIONS: Record<AssignmentPhase, AssignmentPermissions> = {
  DRAFT: {
    canEditMetadata: true,
    canEditQuestions: true,
    canDelete: true,
    canOpenBuilder: true,
    canDuplicate: true,
  },
  SCHEDULED: {
    canEditMetadata: true,
    canEditQuestions: true,
    canDelete: true,
    canOpenBuilder: true,
    canDuplicate: true,
  },
  PUBLISHED_ACTIVE: {
    canEditMetadata: true,
    canEditQuestions: false,
    canDelete: true,
    canOpenBuilder: true,
    canDuplicate: true,
  },
  PUBLISHED_DUE: {
    canEditMetadata: true,
    canEditQuestions: false,
    canDelete: true,
    canOpenBuilder: true,
    canDuplicate: true,
  },
};

export function isAssignmentDue(assignment: AssignmentPermissionContext, now: Date = new Date()): boolean {
  if (assignment.status !== 'PUBLISHED') {
    return false;
  }

  if (typeof assignment.isDue === 'boolean') {
    return assignment.isDue;
  }

  if (!assignment.due_at) {
    return false;
  }

  return new Date(assignment.due_at).getTime() <= now.getTime();
}

export function getAssignmentPhase(assignment: AssignmentPermissionContext, now: Date = new Date()): AssignmentPhase {
  if (assignment.status === 'DRAFT') {
    return 'DRAFT';
  }

  if (assignment.status === 'SCHEDULED') {
    return 'SCHEDULED';
  }

  return isAssignmentDue(assignment, now) ? 'PUBLISHED_DUE' : 'PUBLISHED_ACTIVE';
}

export function getAssignmentPermissions(
  assignment: AssignmentPermissionContext,
  now: Date = new Date(),
): AssignmentPermissions {
  return getAssignmentPermissionsByPhase(getAssignmentPhase(assignment, now));
}

export function getAssignmentPermissionsByPhase(phase: AssignmentPhase): AssignmentPermissions {
  return ASSIGNMENT_PERMISSIONS[phase];
}
